/**
 * Data Integrity Utils - Frontend
 * 
 * Provides utilities to filter/block test data from rendering
 * and logging issues silently.
 */

// Blocked words pattern
const BLOCKED_WORDS = [
  'test', 'teste', 'testing', 'demo', 'mock', 'fake', 'sample',
  'produção', 'producao', 'final', 'temp', 'tmp', 'dummy', 'placeholder',
  'leandro test', 'test links', 'admin teste'
];

const BLOCKED_PATTERN = new RegExp(
  `\\b(${BLOCKED_WORDS.join('|')})\\b`,
  'i'
);

/**
 * Check if text contains blocked test words
 */
export function containsTestData(text) {
  if (!text || typeof text !== 'string') return false;
  return BLOCKED_PATTERN.test(text);
}

/**
 * Filter out test items from an array
 * @param {Array} items - Array of items to filter
 * @param {string[]} fieldsToCheck - Fields to check for test data
 * @returns {Array} Filtered array without test items
 */
export function filterTestData(items, fieldsToCheck = ['name', 'title', 'description']) {
  if (!Array.isArray(items)) return items;
  
  return items.filter(item => {
    for (const field of fieldsToCheck) {
      const value = item?.[field];
      if (value && containsTestData(value)) {
        // Log silently for monitoring
        console.warn(`[DATA_INTEGRITY] Filtered test item: ${field}="${value}"`);
        return false;
      }
    }
    return true;
  });
}

/**
 * Sanitize a single item - returns null if it's test data
 * @param {Object} item - Item to check
 * @param {string[]} fieldsToCheck - Fields to check
 * @returns {Object|null} Original item or null if test data
 */
export function sanitizeItem(item, fieldsToCheck = ['name', 'title', 'description']) {
  if (!item) return item;
  
  for (const field of fieldsToCheck) {
    const value = item?.[field];
    if (value && containsTestData(value)) {
      console.warn(`[DATA_INTEGRITY] Blocked test item: ${field}="${value}"`);
      return null;
    }
  }
  return item;
}

/**
 * Get safe display value - returns fallback if test data
 * @param {string} value - Value to check
 * @param {string} fallback - Fallback value if test data
 * @returns {string} Safe value or fallback
 */
export function getSafeValue(value, fallback = '') {
  if (!value || containsTestData(value)) {
    if (value && containsTestData(value)) {
      console.warn(`[DATA_INTEGRITY] Replaced test value: "${value}" with fallback`);
    }
    return fallback;
  }
  return value;
}

/**
 * Validate product data before rendering
 */
export function validateProduct(product) {
  if (!product) return null;
  
  if (containsTestData(product.name) || containsTestData(product.description)) {
    console.warn(`[DATA_INTEGRITY] Blocked test product: ${product.name}`);
    return null;
  }
  
  return product;
}

/**
 * Validate category data before rendering
 */
export function validateCategory(category) {
  if (!category) return null;
  
  if (containsTestData(category.name)) {
    console.warn(`[DATA_INTEGRITY] Blocked test category: ${category.name}`);
    return null;
  }
  
  return category;
}

/**
 * Filter products array - removes test products
 */
export function filterProducts(products) {
  if (!Array.isArray(products)) return products;
  return products.filter(p => validateProduct(p) !== null);
}

/**
 * Filter categories array - removes test categories
 */
export function filterCategories(categories) {
  if (!Array.isArray(categories)) return categories;
  return categories.filter(c => validateCategory(c) !== null);
}

/**
 * Check if running in production
 */
export function isProduction() {
  return process.env.NODE_ENV === 'production' || 
         window.location.hostname.includes('kaisosystem.com') ||
         window.location.hostname.includes('onrender.com');
}

/**
 * Apply data integrity filter only in production
 */
export function filterInProduction(items, fieldsToCheck = ['name', 'title', 'description']) {
  if (!isProduction()) return items;
  return filterTestData(items, fieldsToCheck);
}

export default {
  containsTestData,
  filterTestData,
  sanitizeItem,
  getSafeValue,
  validateProduct,
  validateCategory,
  filterProducts,
  filterCategories,
  filterInProduction,
  isProduction
};
